"use client";

import { useEffect, useState, type ReactNode } from "react";
import { Info, TrendingDown, TrendingUp, Scale, Wallet, Percent, Landmark, type LucideIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import {
  buildFinancialHealthSummary,
  partStrengthLabel,
  type FinancialHealth,
  type FinancialHealthPart,
} from "@/lib/financial-health";
import { formatCompactCurrency, formatPercent, formatRatio } from "@/lib/format";
import { HistoricalLineChart } from "@/components/charts/historical-line-chart";
import { describeTrend } from "@/lib/trend-summary";
import type { MoatTrendMetric } from "@/lib/quality/moat-trend";
import type { YearValue } from "@/lib/data/types";
import { cn } from "@/lib/utils";

/** Feedback-Runde 19 Punkt 14: die Tooltips waren bisher rein beschreibender Text ohne
 * jede Zahl - jetzt Funktionen statt fester Strings, interpolieren den echten
 * `part.rawValue` (siehe lib/financial-health.ts) direkt in den Erklaertext. */
const PART_TOOLTIPS: Record<string, (rawValue: number) => string> = {
  debtToEquity: (v) =>
    `Verzinsliche Schulden im Verhältnis zum bilanziellen Eigenkapital - je niedriger, desto weniger ist das Unternehmen über Fremdkapital finanziert. Aktueller Wert: ${formatRatio(v)}.`,
  netDebtToEbitda: (v) =>
    `Nettoverschuldung im Verhältnis zum operativen Gewinn (EBITDA) - zeigt, wie schnell die Schulden aus dem laufenden Geschäft tilgbar wären, unabhängig von der Bilanzstruktur. Aktuell das ${formatRatio(v)}-Fache des EBITDA.`,
  cashCushion: (v) =>
    `Wie stark die Gesamtverschuldung durch verfügbare liquide Mittel gedeckt ist - ein hoher Puffer bedeutet, dass Schulden im Zweifel schnell abgelöst werden könnten. Aktuell ${formatPercent(v)} der Schulden durch liquide Mittel gedeckt.`,
  netMargin: (v) =>
    `Nettomarge - wie viel vom Umsatz als Gewinn übrig bleibt und damit langfristig zur Schuldenbedienung zur Verfügung steht. Aktuelle Nettomarge: ${formatPercent(v)}.`,
};

const PART_ICONS: Record<string, LucideIcon> = {
  debtToEquity: Percent,
  netDebtToEbitda: Landmark,
  cashCushion: Wallet,
  netMargin: TrendingUp,
};

/** Eine Akzentfarbe je Teil-Score, aus derselben Palette wie die uebrigen Mehrlinien-
 * Charts des Projekts (Navy/Gold/Gruen/Rot) - macht die Karten auf einen Blick
 * unterscheidbar. netDebtToEbitda nutzt bewusst denselben Rotton wie die
 * "Gesamtverschuldung im Zeitverlauf"-Chart weiter unten (thematisch beides Verschuldung). */
const PART_COLORS: Record<string, string> = {
  debtToEquity: "#16265C",
  netDebtToEbitda: "#B4544C",
  cashCushion: "#C6A15B",
  netMargin: "#3F7A5B",
};

/** Fuenf Zonen exakt entlang der HEALTH_LABELS-Schwellen in lib/financial-health.ts
 * (0/20/40/60/80) - dieselben Grenzen, die vorher nur im Code steckten, werden hier
 * erstmals auch optisch als Zonen sichtbar statt nur als verstecktes Label. */
const HEALTH_ZONES = ["Kritisch", "Angespannt", "Durchschnittlich", "Gesund", "Sehr gesund"];

/**
 * Komplett neu gestaltete Gesamtscore-Anzeige (2026-07-23, Erik: "kannst du es frei
 * gestalten", siehe backlog.md -> Feedback-Runde 17 Punkt 6 / Runde 16 Punkt 7) - ersetzt
 * den vorherigen Halbkreis-Tacho durch eine horizontale Zonen-Leiste: dieselbe Rot-Gold-
 * Gruen-Farblogik, aber jetzt mit sichtbaren Zonengrenzen statt einer verborgenen
 * Tooltip-Erklaerung, und deutlich kompakter (weniger Vertikalraum als der 264px-Halbkreis).
 */
function HealthMeterBar({ score, label }: { score: number; label: string }) {
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setAnimated(true), 0);
    return () => clearTimeout(id);
  }, []);

  const clamped = Math.max(0, Math.min(100, score));
  const position = animated ? clamped : 0;

  return (
    <div className="flex w-full max-w-md flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-4xl text-ink">{Math.round(clamped)}</span>
        <span className="text-sm text-muted-foreground">/ 100</span>
        <span className="font-tabular text-sm font-medium text-ink">· {label}</span>
      </div>
      <div className="relative h-3 w-full rounded-full bg-hairline">
        <div
          className="h-3 rounded-full"
          style={{
            width: "100%",
            background: "linear-gradient(90deg, #B4453B 0%, #D9A441 50%, #3F7A5B 100%)",
          }}
        />
        <div
          className="absolute top-1/2 h-5 w-1.5 -translate-y-1/2 rounded-full bg-ink shadow-[0_0_0_2px_var(--background)] transition-[left] duration-1000 ease-out motion-reduce:duration-0"
          style={{ left: `calc(${position}% - 3px)` }}
          aria-hidden="true"
        />
      </div>
      <div className="flex justify-between text-[0.65rem] text-muted-foreground">
        {HEALTH_ZONES.map((zone) => (
          <span key={zone} className={cn(zone === label && "font-semibold text-ink")}>
            {zone}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Kompakter Trend-Badge (Pfeil + Prozentpunkt-Aenderung) fuer Teil-Scores mit echter
 * Mehrjahres-Historie (aktuell nur Nettomarge, siehe FinancialHealthGauge-Props). Nutzt
 * bewusst denselben, bereits an anderer Stelle (Qualitaetscheck-Tab "Moat-/Qualitaets-
 * Trend") berechneten MoatTrendMetric-Wert statt einer eigenen Neuberechnung. */
function PartTrendBadge({ trend }: { trend: MoatTrendMetric }) {
  const tone = trend.direction === "improving" ? "text-positive" : trend.direction === "declining" ? "text-negative" : "text-muted-foreground";
  const Icon = trend.direction === "declining" ? TrendingDown : TrendingUp;
  const sign = trend.change > 0 ? "+" : "";
  const years = trend.endYear - trend.startYear;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[0.65rem] font-medium ${tone}`}>
      {trend.direction !== "stable" && <Icon className="h-3 w-3" />}
      {sign}
      {trend.change.toFixed(1)} Pp ({years}J)
    </span>
  );
}

/** Stat-Karte je Teil-Score - ersetzt sowohl den vorherigen Mini-Ring als auch die
 * separate, gestapelte Beitrags-Leiste darunter: Gewicht, Punktzahl, Staerke-Einordnung
 * UND der tatsaechliche Punktbeitrag zum Gesamtscore stehen jetzt an einer Stelle
 * zusammen statt auf zwei getrennte Elemente verteilt. */
function PartStatCard({ part, trend }: { part: FinancialHealthPart; trend?: MoatTrendMetric }) {
  const color = PART_COLORS[part.key] ?? "#16265C";
  const Icon = PART_ICONS[part.key] ?? Scale;
  const contribution = part.score * part.weight;

  return (
    <Card className="flex-1 border-hairline">
      <CardContent className="flex flex-col gap-2 pt-5">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm text-ink-secondary">
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: `${color}1A`, color }}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            {part.label}
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label={`Erklärung: ${part.label}`} className="text-muted-foreground hover:text-ink">
                  <Info className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">{PART_TOOLTIPS[part.key]?.(part.rawValue)}</TooltipContent>
            </Tooltip>
          </span>
          {trend && <PartTrendBadge trend={trend} />}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-tabular text-2xl font-semibold text-ink">{Math.round(part.score)}</span>
          <span className="text-xs text-muted-foreground">/ 100 · {partStrengthLabel(part.score)}</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-hairline">
          <div
            className="h-full rounded-full transition-[width] duration-1000 ease-out motion-reduce:duration-0"
            style={{ width: `${Math.max(0, Math.min(100, part.score))}%`, backgroundColor: color }}
          />
        </div>
        <span className="text-[0.65rem] text-muted-foreground">
          Gewicht {Math.round(part.weight * 100)} % · trägt {contribution.toFixed(0)} von 100 Punkten bei
        </span>
      </CardContent>
    </Card>
  );
}

export function FinancialHealthGauge({
  health,
  currency = "USD",
  totalDebtHistory = [],
  aiExplanation = null,
  netMarginTrend,
}: {
  health: FinancialHealth;
  currency?: string;
  /** Fuer die kleine Verschuldungs-Trend-Grafik - optional, da nicht jede Aktie eine
   * mehrjaehrige totalDebt-Historie hat. */
  totalDebtHistory?: YearValue[];
  /** KI-Erklaerung, warum der Score so ausfaellt - optional/null ohne GEMINI_API_KEY oder
   * bei Fehler. ReactNode statt string: der Aufrufer uebergibt einen eigenen, in
   * <Suspense> gerahmten Server-Component-Slot statt den bereits aufgeloesten KI-Text. */
  aiExplanation?: ReactNode;
  /** Nettomarge-Trend aus computeMoatTrend() (lib/quality/moat-trend.ts), vom Aufrufer
   * wiederverwendet statt hier neu berechnet. Nur diese Teilkennzahl hat eine echte
   * Mehrjahres-Historie (debtToEquity/cashCushion kennen nur den aktuellen Wert). */
  netMarginTrend?: MoatTrendMetric;
}) {
  const summary = buildFinancialHealthSummary(health);
  const sortedDebtHistory = [...totalDebtHistory].sort((a, b) => a.year - b.year);
  const debtTrend = describeTrend("Gesamtverschuldung", sortedDebtHistory, "currency", currency);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-1">
        <span className="inline-flex items-center gap-1.5">
          <h3 className="text-sm font-medium text-ink-secondary">Finanzielle Gesundheit</h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Erklärung: Finanzielle Gesundheit" className="text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">
              Heuristischer Indikator aus Schuldenquote, Net Debt/EBITDA, Deckung der
              Schulden durch liquide Mittel und Nettomarge – je zu 25 %, anteilig gewichtet
              je nach Datenlage. Unabhängig vom Erikonomie-Score oben auf der Seite.
            </TooltipContent>
          </Tooltip>
        </span>
        <HealthMeterBar score={health.score} label={health.label} />
      </div>

      {health.parts.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row">
          {health.parts.map((part) => (
            <PartStatCard key={part.key} part={part} trend={part.key === "netMargin" ? netMarginTrend : undefined} />
          ))}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">{summary}</p>
      {health.missingPartsNote && (
        <p className="text-center text-xs text-muted-foreground">{health.missingPartsNote}</p>
      )}
      {aiExplanation}

      {sortedDebtHistory.length >= 2 && (
        <div className="border-t border-hairline pt-5">
          <h3 className="mb-3 text-sm font-medium text-ink-secondary">Gesamtverschuldung im Zeitverlauf</h3>
          <HistoricalLineChart
            data={sortedDebtHistory}
            format="currency"
            currency={currency}
            color="#B4544C"
            variant="bar"
            valueLabel="Gesamtverschuldung"
            referenceValue={health.estimatedCash}
            referenceLabel={
              health.estimatedCash !== undefined
                ? `Geschätzte liquide Mittel: ${formatCompactCurrency(health.estimatedCash, currency)}`
                : undefined
            }
          />
          {debtTrend && <p className="mt-2 text-xs text-muted-foreground">{debtTrend}</p>}
        </div>
      )}

      {sortedDebtHistory.length < 2 && health.estimatedCash !== undefined && (
        <p className="text-center text-xs text-muted-foreground">
          Geschätzte liquide Mittel (Gesamtverschuldung − Nettoverschuldung):{" "}
          <span className="font-tabular text-ink-secondary">
            {formatCompactCurrency(health.estimatedCash, currency)}
          </span>
        </p>
      )}
    </div>
  );
}
